import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { TextInput } from '../../shared/components/TextInput';
import { css, cx } from '@emotion/css';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { EmailSignInParams } from '../../shared/types/input-param-types';
import { h1Style } from '../../shared/styles/text';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Layout } from '../../shared/components/layout/Layout';
import { useNavigator } from '../../routing/navigator';
import { useQueryClient } from '@tanstack/react-query';
import { CacheKeys } from '../../shared/services/api/cache-configuration';
import { authenticationStore } from '../../shared/stores/authentification-store';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { appRoutes } from '../../routing/app-routes';
import { showPublicToast } from '../../shared/utils/show-public-toast';
import { useEmailSignIn } from '../../shared/services/api/user';
import {
  authButtonContainerStyle,
  authFormContentStyle,
  authHeaderStyle,
  authInlineLinkStyle,
  authSubtitleStyle,
} from '../../shared/styles/auth-layout';
import { Link } from 'react-router-dom';

const validationSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(8, 'Password is too short').required(),
});

export const EmailSignInPage = observer((): JSX.Element => {
  const signInUser = useEmailSignIn();
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const authData = authenticationStore.getAuthenticationData();
  const [signingIn, setSigningIn] = useState(false);

  /**
   * Redirect in case of manually entered URL.
   */
  useEffect(() => {
    const email = authData?.email;
    if (!email) {
      navigator.toCheckEmail();
    }
  }, []);

  const methods = useForm({
    defaultValues: {
      email: authData?.email ? authData.email : '',
      password: '',
    } as EmailSignInParams,
    resolver: yupResolver(validationSchema) as any,
    mode: 'all',
  });

  /**
   * Handle the sign-in logic for the user.
   * @param data
   */
  const handleSignInUser: SubmitHandler<EmailSignInParams> = async (
    data: EmailSignInParams,
  ) => {
    try {
      setSigningIn(true);
      const { token } = await signInUser({ ...data });
      window.localStorage.setItem('token', token);
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.ME] });
      authenticationStore.reset();
      navigator.toLanding();
    } catch (err: any) {
      showPublicToast('Incorrect credentials or unverified.');
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSignInUser)}>
        <Layout.Content className={authFormContentStyle}>
          <div>
            <h1 className={cx(h1Style, authHeaderStyle)}>Welcome back.</h1>
            <p className={authSubtitleStyle}>Enter your password to sign in.</p>
            <TextInput
              name={'email'}
              placeHolder={'enter your email'}
              type={'email'}
            />
            <div>
              <TextInput
                name={'password'}
                placeHolder={'password'}
                type={'password'}
              />
              <div className={forgotRowStyle}>
                <Link
                  to={appRoutes.forgotPassword}
                  className={authInlineLinkStyle}
                >
                  forgot password?
                </Link>
              </div>
            </div>
          </div>
          <div className={authButtonContainerStyle}>
            <Button
              variant={ButtonVariantEnum.TEXT}
              size={ButtonSizeEnum.LARGE}
              text={'back'}
              onClick={navigator.toPrevious}
            />
            <Button
              variant={ButtonVariantEnum.FILLED}
              size={ButtonSizeEnum.LARGE}
              text={'next'}
              type={'submit'}
              loading={signingIn}
            />
          </div>
        </Layout.Content>
      </form>
    </FormProvider>
  );
});

const forgotRowStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-top: -0.5rem;
`;
